import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { EPrescriptionLogin } from '@/types/settings-api';

/**
 * Practice eRx login (Practice Details tab).
 *
 * Sister flow to the Provider eRx in `use-doctor.ts`. Same `EPrescriptionLogin`
 * type, different paths:
 *   - GET  /api/Practice/EPrescriptionLogin?practiceId={id}  — single row, NOT array
 *   - POST /api/Practice/AddUpdateEPrescriptionLogin         — upsert keyed on body.practiceId
 *
 * Wire-shape verified against dev 2026-05-20 (see
 * `docs/CONVERSIONS/settings-practice-erx/CONVERSION_NOTES.md` §6.1):
 *   - GET response: `{id, practiceId, eClaimEnabled, ePrescriptionEnabled,
 *     username, password}` with `providerId` / `practiceName` /
 *     `practiceImage` OMITTED on Practice-scoped responses. Username and
 *     password come back as empty string `""` when never submitted.
 *   - POST body: full state every time (no `if (!= null)` omission — match
 *     the captured live Flutter wire, NOT the Flutter source's toJson
 *     null-juggling).
 *   - POST response: JSON-quoted string of the row's id (`"a0b44b3c-..."`,
 *     38 bytes). `apiClient.parseResponse<T>` JSON-parses to the unquoted
 *     GUID.
 *   - Upsert key is `body.practiceId`. No `id`-in-body, no Create/Edit
 *     wire distinction.
 *
 * The backend does NOT enforce cross-toggle coupling — sending
 * `{eClaimEnabled: true, ePrescriptionEnabled: false}` is stored verbatim.
 * The component layer applies the Flutter coupling rules client-side
 * (see `practice-erx-section.tsx`).
 */

export function usePracticeEPrescriptionLogin(
  practiceId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['settings', 'practice-erx-login', practiceId],
    enabled: !!practiceId,
    queryFn: ({ signal }) => {
      if (!practiceId) {
        throw new Error('usePracticeEPrescriptionLogin called without practiceId');
      }
      const qs = new URLSearchParams({ practiceId });
      return get<EPrescriptionLogin | null>(
        `/api/Practice/EPrescriptionLogin?${qs.toString()}`,
        { signal },
      );
    },
  });
}

/**
 * PHI handling — `gcTime: 0` evicts `mutation.variables` (which carries the
 * plaintext eClaim username + password) from React Query's MutationCache
 * the moment the request settles. Without this, RQ retains the variables
 * for the default 5-minute gcTime, where they'd be visible in the RQ
 * Devtools `Mutations` panel and reachable from any JS context with a
 * handle on the client. Mirrors `use-upload-image.ts:53-57` (PROD-725,
 * Samer-hardened) which set the same precedent for base64 PHI payloads.
 * Settled credentials live only in the consumer component's local
 * `useState`; no `localStorage` / `sessionStorage` / analytics path.
 */
export function useSavePracticeEPrescriptionLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (login: EPrescriptionLogin) =>
      post<string>('/api/Practice/AddUpdateEPrescriptionLogin', login),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'practice-erx-login', vars.practiceId],
      });
    },
  });
}
